'use client'

import { MutableRefObject } from 'react'

import type { KeenSliderInstance, KeenSliderHooks } from 'keen-slider/react'
import { useEffect, useState, useRef } from 'react'

/**
 * --------------------------------------------------------------------------
 * KeenSliderFade - Plugin for KeenSlider to fade slides in and out
 * --------------------------------------------------------------------------
 **/
export function KeenSliderFade(slider) {
  // On drag/change, update the opacity of each slide based on its progress
  slider.on('detailsChanged', (details) => {
    // If there are no details or slides, or if the slider is disabled, reset all slides
    if (!details || !details.slides || details.options.disabled) {
      details?.slides.map((slide, index) => {
        slide.style.opacity = '1'
        slide.style.pointerEvents = 'auto'
        slide.style.visibility = 'visible'
        slide.style.zIndex = index === 0 ? '1' : '0' // make the first slide visible
        slide.dataset.ariaHidden = 'false'
      })
      return
    }

    // Iterate through each slide and set its opacity based on its progress
    details?.track?.details?.slides.map((slide, index) => {
      const progress = slide.portion
      details.slides[index].style.opacity = `${progress}`
      details.slides[index].style.pointerEvents = progress !== 0 ? 'auto' : 'none'
      details.slides[index].style.visibility = progress !== 0 ? 'visible' : 'hidden'
      details.slides[index].style.zIndex = progress !== 0 ? '1' : '0'
      details.slides[index].ariaHidden = progress !== 0 ? 'false' : 'true'
    })
  })
}

/**
 * --------------------------------------------------------------------------
 * KeenSliderArrows - Plugin for KeenSlider to add arrow navigation
 * --------------------------------------------------------------------------
 **/

export function KeenSliderArrows({
  sliderInstance,
  disabled,
}: {
  sliderInstance: MutableRefObject<KeenSliderInstance<object, object, KeenSliderHooks>>
  disabled?: boolean
}) {
  const [prevDisabled, setPrevDisabled] = useState(true)
  const [nextDisabled, setNextDisabled] = useState(false)

  useEffect(() => {
    if (!sliderInstance?.current) return

    const currentSlider = sliderInstance.current

    const updateSlideState = () => {
      const details = currentSlider.track.details
      const currentSlide = details.abs ?? 0
      const totalSlides = details.slides.length
      const slidesOption = currentSlider?.options?.slides

      const slidesPerView =
        typeof slidesOption === 'object' &&
        slidesOption !== null &&
        'perView' in slidesOption &&
        Number(slidesOption.perView)
          ? Number(slidesOption.perView)
          : 1

      setPrevDisabled(currentSlide === 0)
      setNextDisabled(currentSlide >= totalSlides - slidesPerView)
    }

    currentSlider.on('detailsChanged', updateSlideState)

    // Initialize the state when the component mounts
    updateSlideState()

    // Cleanup the event listener on unmount or when `sliderInstance` changes
    return () => {
      currentSlider.on('detailsChanged', updateSlideState, true) // true = remove
    }
  }, [sliderInstance])

  if (disabled || !sliderInstance || !sliderInstance.current) {
    return null
  }

  const slidesOption = sliderInstance.current.options.slides
  const perView =
    typeof slidesOption === 'object' && slidesOption !== null ? (slidesOption.perView ?? 1) : 1
  const totalSlides = sliderInstance?.current.slides.length

  if (totalSlides <= Number(perView)) {
    return null
  }

  return (
    <div className="keen-slider__arrows mt-m flex justify-end gap-xs">
      <KeenSliderArrow
        left
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          sliderInstance.current?.prev()
        }}
        disabled={prevDisabled}
      />

      <KeenSliderArrow
        onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
          e.stopPropagation()
          sliderInstance.current?.next()
        }}
        disabled={nextDisabled}
      />
    </div>
  )
}

type KeenSliderArrowProps = {
  left?: boolean
  onClick: (e: React.MouseEvent<HTMLButtonElement>) => void
  disabled: boolean
}

function KeenSliderArrow(props: KeenSliderArrowProps) {
  const type = props.left ? 'left' : 'right'
  const direction = type === 'left' ? 'previous' : 'next'

  return (
    <button
      onClick={props.onClick}
      aria-disabled={props.disabled}
      aria-label={`${direction} slide`}
      className={`keen-slider__arrow keen-slider__arrow-${type} flex h-40 w-40 items-center justify-center rounded-full border bg-theme text-fg-title transition-colors duration-150 theme--base-0 [&[aria-disabled="true"]]:pointer-events-none [&[aria-disabled="true"]]:text-fg-title/15 [&[aria-disabled="true"]]:opacity-60`}>
      <svg
        aria-hidden="true"
        className={`h-[32px] w-[32px] ${type === 'left' ? 'ml-[-3px]' : 'mr-[-3px]'}`}>
        <use href={`/img/sprite.svg#icon-slider-arrow-${type}`}></use>
      </svg>
    </button>
  )
}

/**
 * --------------------------------------------------------------------------
 * KeenSliderAutoplay - Legacy function interface for backward compatibility
 * --------------------------------------------------------------------------
 * This maintains compatibility with the original plugin interface
 * while preventing re-execution on every render.
 **/
export function KeenSliderAutoplay(slider) {
  if (!slider || !slider.options.autoplay) return

  // Create a static map to track initialized sliders
  if (!KeenSliderAutoplay.initialized) {
    KeenSliderAutoplay.initialized = new WeakMap()
  }

  // Only set up autoplay once per slider instance
  if (KeenSliderAutoplay.initialized.get(slider)) {
    return
  }

  KeenSliderAutoplay.initialized.set(slider, true)
  const interval = slider.options.autoplay ?? 1000

  let paused = false
  let isHovered = false
  let timeout: ReturnType<typeof setTimeout>

  function clearNextTimeout() {
    clearTimeout(timeout)
  }

  function nextTimeout() {
    clearNextTimeout()
    if (paused || isHovered || slider.options.disabled) return

    timeout = setTimeout(() => {
      if (paused) return
      slider.next()
    }, interval)
  }

  // Set up event listeners
  slider.on('created', () => {
    // Delayed initialization to stop pagespeed issues
    setTimeout(() => {
      const onMouseOver = () => {
        isHovered = true
        paused = true
        clearNextTimeout()
      }

      const onMouseOut = () => {
        isHovered = false
        paused = false
        nextTimeout()
      }

      slider.container.addEventListener('mouseover', onMouseOver)
      slider.container.addEventListener('mouseout', onMouseOut)

      // Store cleanup callback on slider instance
      slider.__autoplayCleanup = () => {
        slider.container.removeEventListener('mouseover', onMouseOver)
        slider.container.removeEventListener('mouseout', onMouseOut)
      }

      nextTimeout()
    }, 2000)
  })

  slider.on('dragStarted', clearNextTimeout)
  slider.on('animationStarted', clearNextTimeout)
  slider.on('animationEnded', () => {
    nextTimeout()
  })
  slider.on('updated', () => {
    nextTimeout()
  })

  // Clean up previous listeners when slider is destroyed
  slider.on('destroyed', () => {
    clearNextTimeout()
    if (slider.__autoplayCleanup) {
      slider.__autoplayCleanup()
    }
    KeenSliderAutoplay.initialized.delete(slider)
  })
}

// Initialize the static property
KeenSliderAutoplay.initialized = new WeakMap()
